import React, { useState, useEffect } from 'react';
import './AutoConexion.css';
import { useI18n } from '../i18n';

const AutoConexion = () => {
  const { language, t } = useI18n();
  const [config, setConfig] = useState({
    temp: 0.5,
    learningRate: 0.5,
    optimism: false,
    sarcasm: false,
    overthinking: false,
    instinct: 0.5
  });

  const [aiAdjustments, setAiAdjustments] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/users/auto-connection', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Q-Language': language,
            'Accept-Language': language
          }
        });
        const data = await res.json();
        if (data.params) {
          setConfig(data.params);
        }
        if (data.ai_adjustments) {
          setAiAdjustments(data.ai_adjustments);
        }
      } catch (err) {
        console.error('Error fetching auto-connection config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [language]);

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    // Si el usuario cambia algo, limpiamos el ajuste de IA para ese parámetro (UI reactiva)
    setAiAdjustments(prev => prev.filter(adj => adj.param !== field));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/auto-connection', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Q-Language': language,
          'Accept-Language': language
        },
        body: JSON.stringify({ params: config })
      });

      if (res.ok) {
        setFeedback(t('autoConnection.saveOk'));
        setAiAdjustments([]); // Asumimos control manual total al guardar
        setTimeout(() => setFeedback(''), 5000);
      } else {
        setFeedback(t('autoConnection.saveError'));
      }
    } catch (err) {
      console.error('Error saving auto-connection config:', err);
      setFeedback(t('autoConnection.networkError'));
    }
  };

  const getSliderColor = (val) => {
    // 0 = Cold (Blue), 1 = Hot (Red/Orange)
    // Blue is around 200, Red/Orange is around 0
    const hue = (1 - val) * 200;
    return `hsl(${hue}, 70%, 50%)`;
  };

  const Tooltip = ({ text }) => (
    <div className="tooltip-container">
      <span className="tooltip-icon">[?]</span>
      <div className="tooltip-text">{text}</div>
    </div>
  );

  if (loading) return <div className="auto-conexion-page">{t('autoConnection.loading')}</div>;

  return (
    <div className="auto-conexion-page">
      <header className="lab-header">
        <h1>{t('autoConnection.title')}</h1>
        <p className="subtitle">{t('autoConnection.subtitle')}</p>
      </header>

      {aiAdjustments.length > 0 && (
        <div className="ai-insight-panel">
          <h3>{t('autoConnection.aiDetected')}</h3>
          <ul>
            {aiAdjustments.map((adj, idx) => {
              const paramLabels = {
                temp: t('autoConnection.params.temp'),
                learningRate: t('autoConnection.params.learningRate'),
                optimism: t('autoConnection.params.optimism'),
                sarcasm: t('autoConnection.params.sarcasm'),
                overthinking: t('autoConnection.params.overthinking'),
                instinct: t('autoConnection.params.instinct')
              };
              return (
                <li key={idx}>
                  {t('autoConnection.adjusted')} <strong>[{paramLabels[adj.param] || adj.param}]</strong> a <strong>[{adj.value}]</strong>: {adj.reason}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="calibration-grid">
        {/* Frame 1 */}
        <div className="lab-frame">
          <h2 className="frame-title">{t('autoConnection.frame1')}</h2>
          <div className="control-group">
            <label>
              {t('autoConnection.responseTemp', { value: config.temp.toFixed(2) })}
              <Tooltip text={t('autoConnection.responseTempTip')} />
            </label>
            <input
              type="range"
              min="0" max="1" step="0.01"
              value={config.temp}
              onChange={(e) => handleChange('temp', parseFloat(e.target.value))}
              style={{ accentColor: getSliderColor(config.temp) }}
            />
            <div className="label-hints">
              <span>{t('autoConnection.deterministic')}</span>
              <span>{t('autoConnection.chaotic')}</span>
            </div>
          </div>
          <div className="control-group">
            <label>
              {t('autoConnection.learningRate', { value: config.learningRate.toFixed(2) })}
              <Tooltip text={t('autoConnection.learningTip')} />
            </label>
            <input
              type="range"
              min="0" max="1" step="0.01"
              value={config.learningRate}
              onChange={(e) => handleChange('learningRate', parseFloat(e.target.value))}
              style={{ accentColor: getSliderColor(config.learningRate) }}
            />
            <div className="label-hints">
              <span>{t('autoConnection.stubborn')}</span>
              <span>{t('autoConnection.adaptable')}</span>
            </div>
          </div>
        </div>

        {/* Frame 2 */}
        <div className="lab-frame">
          <h2 className="frame-title">{t('autoConnection.frame2')}</h2>
          <div className="control-group switch-group">
            <label>
              {t('autoConnection.optimism')}
              <Tooltip text={t('autoConnection.optimismTip')} />
            </label>
            <button
              className={`techno-switch ${config.optimism ? 'on' : 'off'}`}
              onClick={() => handleChange('optimism', !config.optimism)}
            >
              <div className="switch-knob" />
            </button>
          </div>
          <div className="control-group switch-group">
            <label>
              {t('autoConnection.sarcasm')}
              <Tooltip text={t('autoConnection.sarcasmTip')} />
            </label>
            <button
              className={`techno-switch ${config.sarcasm ? 'on' : 'off'}`}
              onClick={() => handleChange('sarcasm', !config.sarcasm)}
            >
              <div className="switch-knob" />
            </button>
          </div>
        </div>

        {/* Frame 3 */}
        <div className="lab-frame">
          <h2 className="frame-title">{t('autoConnection.frame3')}</h2>
          <div className="control-group switch-group">
            <label>
              {t('autoConnection.overthinking')}
              <Tooltip text={t('autoConnection.overthinkingTip')} />
            </label>
            <button
              className={`techno-switch ${config.overthinking ? 'on' : 'off'}`}
              onClick={() => handleChange('overthinking', !config.overthinking)}
            >
              <div className="switch-knob" />
            </button>
          </div>
          <div className="control-group">
            <label>
              {t('autoConnection.instinct', { value: config.instinct.toFixed(2) })}
              <Tooltip text={t('autoConnection.instinctTip')} />
            </label>
            <input
              type="range"
              min="0" max="1" step="0.01"
              value={config.instinct}
              onChange={(e) => handleChange('instinct', parseFloat(e.target.value))}
              style={{ accentColor: getSliderColor(config.instinct) }}
            />
            <div className="label-hints">
              <span>{t('autoConnection.rational')}</span>
              <span>{t('autoConnection.intuitive')}</span>
            </div>
          </div>
        </div>
      </div>

      <footer className="lab-footer">
        {feedback && <div className="feedback-message">{feedback}</div>}
        <button className="save-btn" onClick={handleSave}>{t('autoConnection.save')}</button>
      </footer>
    </div>
  );
};

export default AutoConexion;
